import { useState, useEffect } from 'react'
import { Loader2 } from 'lucide-react'
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from '@/shared/components/ui/sheet'
import { Button } from '@/shared/components/ui/button'
import { Input } from '@/shared/components/ui/input'
import { Label } from '@/shared/components/ui/label'
import { Textarea } from '@/shared/components/ui/textarea'
import { Switch } from '@/shared/components/ui/switch'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/shared/components/ui/select'
import { useCreateConfiguracionCEMC, useUpdateConfiguracionCEMC } from '../hooks/useCEMC'
import type { ConfiguracionCEMC } from '../types'

interface Props {
  open: boolean
  onClose: () => void
  grupos: { id: number; name: string }[]
  editing?: ConfiguracionCEMC | null
}

export function ConfiguracionFormSheet({ open, onClose, grupos, editing }: Props) {
  const [nombre, setNombre] = useState('')
  const [titulo, setTitulo] = useState('CEMC - Educación Médica Continua')
  const [descripcion, setDescripcion] = useState('')
  const [emailBlacklist, setEmailBlacklist] = useState('')
  const [grupoId, setGrupoId] = useState<string>('')
  const [activo, setActivo] = useState(true)

  const createMutation = useCreateConfiguracionCEMC()
  const updateMutation = useUpdateConfiguracionCEMC()
  const isLoading = createMutation.isPending || updateMutation.isPending

  useEffect(() => {
    if (editing) {
      setNombre(editing.nombre)
      setTitulo(editing.titulo_reporte)
      setDescripcion(editing.descripcion)
      setEmailBlacklist(editing.email_blacklist ?? '')
      setGrupoId(String(editing.grupo_id))
      setActivo(editing.activo)
    } else {
      setNombre('')
      setTitulo('CEMC - Educación Médica Continua')
      setDescripcion('')
      setEmailBlacklist('')
      setGrupoId('')
      setActivo(true)
    }
  }, [editing, open])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    const payload = {
      nombre,
      titulo_reporte: titulo,
      descripcion,
      email_blacklist: emailBlacklist,
      grupo_id: Number(grupoId),
      activo,
    }
    try {
      if (editing) {
        await updateMutation.mutateAsync({ id: editing.id, payload })
      } else {
        await createMutation.mutateAsync(payload)
      }
      onClose()
    } catch {}
  }

  return (
    <Sheet open={open} onOpenChange={onClose}>
      <SheetContent className="w-full sm:max-w-lg overflow-y-auto">
        <SheetHeader>
          <SheetTitle>{editing ? 'Editar Configuración' : 'Nueva Configuración CEMC'}</SheetTitle>
        </SheetHeader>
        <form onSubmit={handleSubmit} className="space-y-4 mt-4">
          <div className="space-y-2">
            <Label>Grupo / Laboratorio</Label>
            <Select value={grupoId} onValueChange={setGrupoId} required>
              <SelectTrigger>
                <SelectValue placeholder="Seleccionar grupo" />
              </SelectTrigger>
              <SelectContent>
                {grupos.map((g) => (
                  <SelectItem key={g.id} value={String(g.id)}>{g.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label>Nombre de la configuración</Label>
            <Input value={nombre} onChange={(e) => setNombre(e.target.value)} required />
          </div>
          <div className="space-y-2">
            <Label>Título del reporte</Label>
            <Input value={titulo} onChange={(e) => setTitulo(e.target.value)} required />
          </div>
          <div className="space-y-2">
            <Label>Descripción</Label>
            <Textarea value={descripcion} onChange={(e) => setDescripcion(e.target.value)} rows={3} />
          </div>
          <div className="space-y-2">
            <Label>Emails excluidos (separados por comas)</Label>
            <Textarea
              value={emailBlacklist}
              onChange={(e) => setEmailBlacklist(e.target.value)}
              rows={3}
              placeholder="test@example.com, prueba@test.com"
            />
            <p className="text-xs text-muted-foreground">
              Estos emails se excluyen de todas las tablas, gráficas y estadísticas del reporte.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Switch checked={activo} onCheckedChange={setActivo} id="activo" />
            <Label htmlFor="activo">Activo</Label>
          </div>
          <div className="flex gap-2 pt-2">
            <Button type="submit" disabled={isLoading || !grupoId} className="flex-1">
              {isLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {editing ? 'Guardar cambios' : 'Crear configuración'}
            </Button>
            <Button type="button" variant="outline" onClick={onClose}>Cancelar</Button>
          </div>
        </form>
      </SheetContent>
    </Sheet>
  )
}
